import { useState, useEffect } from 'react';
import { useParams, Link } from '../components/Router';
import { Header } from '../components/Header';
import { carPricesApi, CarModelWithBrand, CarModelVersion } from '../lib/carPricesApi';
import { submitQuoteRequest } from '../lib/supabase';
import { LayoutWithBanners } from '../components/LayoutWithBanners';
import ReCAPTCHA from "react-google-recaptcha";
import { slugify } from '../utils/slugify'; 
import { 
  X, ChevronLeft, ChevronRight, Info, CheckCircle, Gauge, Fuel, Settings, 
  ChevronDown, Car, Package, Zap, Shield, ArrowRight, Tag, Mail, Phone, 
  User, AlertCircle, MapPin, MessageSquare, Cpu, Battery, Briefcase // <-- Add Briefcase
} from 'lucide-react';

export function CarModelDetailsPage() {
  const { brandSlug, modelSlug, versionSlug } = useParams<{ brandSlug: string; modelSlug: string; versionSlug?: string }>();
  const [model, setModel] = useState<CarModelWithBrand | null>(null);
  const [versions, setVersions] = useState<CarModelVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<CarModelVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedSpecCategories, setExpandedSpecCategories] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'features'>('overview');
  
  // Quote request modal state
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    phone: '',
    email: '',
    city: '',
    message: '',
    recaptchaToken: ''
  });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  // Section titles state
  const [sectionTitles, setSectionTitles] = useState<Record<string, string>>({
    'Carrosserie': 'Carrosserie',
    'Dimensions': 'Dimensions',
    'Motorisation': 'Motorisation',
    'Transmission': 'Transmission',
    'Performances': 'Performances',
    'Consommation': 'Consommation',
    'Equipements de sécurité': 'Equipements de sécurité',
    'Aides à la conduite': 'Aides à la conduite',
    'Audio et communication': 'Audio et communication',
    'Équipements': 'Équipements'
  });

  useEffect(() => {
    if (modelSlug) {
      loadModel();
    }
  }, [modelSlug, brandSlug, versionSlug]);

  useEffect(() => {
    if (versionSlug && versions.length > 0) {
      // Try to find version by exact slug match first
      let version = versions.find(v => v.slug === versionSlug || v.id === versionSlug);
      
      // If not found, try fuzzy match on name (in case URL slug is cleaner than DB slug)
      if (!version) {
        version = versions.find(v => slugify(v.name) === versionSlug);
      }

      if (version) {
        setSelectedVersion(version);
      }
    }
  }, [versionSlug, versions]);

  const loadModel = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // --- SMART LOOKUP LOGIC START ---
      let modelData = await carPricesApi.getModel(modelSlug);

      // If direct lookup failed (because URL is "serie-1" but DB is "s-rie-1")
      if (!modelData && brandSlug) {
        console.log('Direct model lookup failed, attempting fuzzy match...');
        // Fetch all models for this brand
        const brandModels = await carPricesApi.getModels(brandSlug);
        
        // Find the one where the slugified name matches our URL
        // We compare slugify(db_name) === url_slug
        modelData = brandModels.find(m => slugify(m.name) === modelSlug) || null;
      }

      if (!modelData) {
        throw new Error('Model not found');
      }
      // --- SMART LOOKUP LOGIC END ---

      // Use the ACTUAL slug from the database (modelData.slug) to fetch versions
      // This ensures the API call works even if modelSlug was different
      const versionsData = await carPricesApi.getModelVersions(modelData.slug || modelData.id);
      
      setModel(modelData);
      setVersions(versionsData);
      
      if (modelData.specifications && modelData.specifications.sectionTitles) {
        setSectionTitles(modelData.specifications.sectionTitles);
      }
    } catch (error) {
      console.error('Error loading model:', error);
      setError('Failed to load model details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'Prix sur demande';
    return `${price.toLocaleString()} TND`;
  };

  const nextImage = () => {
    const images = selectedVersion ? selectedVersion.images : model?.images;
    if (images && images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % images!.length);
    }
  };

  const prevImage = () => {
    const images = selectedVersion ? selectedVersion.images : model?.images;
    if (images && images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + images!.length) % images!.length);
    }
  };

  const isObject = (value: any): boolean => {
    return value && typeof value === 'object' && !Array.isArray(value);
  };

  const toggleSpecCategory = (category: string) => {
    setExpandedSpecCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryIcon = (category: string) => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('moteur') || lowerCategory.includes('motorisation')) {
      return <Gauge className="w-4 h-4" />;
    } else if (lowerCategory.includes('transmission') || lowerCategory.includes('boîte')) {
      return <Settings className="w-4 h-4" />;
    } else if (lowerCategory.includes('dimension')) {
      return <Car className="w-4 h-4" />;
    } else if (lowerCategory.includes('performance')) {
      return <Zap className="w-4 h-4" />;
    } else if (lowerCategory.includes('sécurité')) {
      return <Shield className="w-4 h-4" />;
    } else if (lowerCategory.includes('équipement')) {
      return <Package className="w-4 h-4" />;
    }
    return <Settings className="w-4 h-4" />;
  };

  const handleRecaptchaChange = (token: string | null) => {
    setQuoteForm({ ...quoteForm, recaptchaToken: token || '' });
  };

  // --- FIXED HANDLE QUOTE SUBMIT ---
  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setQuoteError('');
    setQuoteSuccess(false);
    
    // Validation
    if (!quoteForm.firstName || !quoteForm.lastName || !quoteForm.phone || !quoteForm.email || !quoteForm.city) {
      setQuoteError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(quoteForm.email)) {
      setQuoteError('Veuillez entrer une adresse email valide');
      return;
    }
    
    if (!quoteForm.recaptchaToken) {
      setQuoteError('Veuillez vérifier que vous n\'êtes pas un robot');
      return;
    }
    
    setQuoteSubmitting(true);
    
try {
      const quoteData = {
        first_name: quoteForm.firstName,
        last_name: quoteForm.lastName,
        company_name: quoteForm.companyName, // <-- Added field
        phone: quoteForm.phone,
        email: quoteForm.email,
        city: quoteForm.city,
        message: quoteForm.message,
        car_model_id: model?.id, 
        // Use selectedVersion specific name if available
        car_model_name: selectedVersion ? `${selectedVersion.name} (${model?.name})` : model?.name || '',
        car_brand_name: model?.brand?.name || '',
      };

      await submitQuoteRequest(quoteData);
      
      setQuoteSuccess(true);
      
      setQuoteForm({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        city: '',
        message: '',
        recaptchaToken: ''
      });

      setTimeout(() => {
        setShowQuoteModal(false);
        setQuoteSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error submitting quote request:', error);
      setQuoteError('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const getFormattedSpecifications = () => {
    if (selectedVersion && selectedVersion.specifications) {
      return formatSpecifications(selectedVersion.specifications);
    }
    if (model && model.specifications) {
      return formatSpecifications(model.specifications);
    }
    return null;
  };

  const formatSpecifications = (specs: any) => {
    const formattedSpecs: Record<string, any> = {};
    if (specs.Carrosserie) formattedSpecs.Carrosserie = specs.Carrosserie;
    if (specs.Dimensions) formattedSpecs.Dimensions = specs.Dimensions;
    if (specs.Motorisation) formattedSpecs.Motorisation = specs.Motorisation;
    if (specs.Transmission) formattedSpecs.Transmission = specs.Transmission;
    if (specs.Performances) formattedSpecs.Performances = specs.Performances;
    if (specs.Consommation) formattedSpecs.Consommation = specs.Consommation;
    if (specs['Équipements de sécurité']) formattedSpecs['Équipements de sécurité'] = specs['Équipements de sécurité'];
    if (specs['Aides à la conduite']) formattedSpecs['Aides à la conduite'] = specs['Aides à la conduite'];
    if (specs['Audio et communication']) formattedSpecs['Audio et communication'] = specs['Audio et communication'];
    if (specs.Équipements) formattedSpecs.Équipements = specs.Équipements;
    return formattedSpecs;
  };

const SpecificationsDisplay = ({ specifications }: { specifications: Record<string, any> }) => {
    if (!specifications || Object.keys(specifications).length === 0) {
      return (
        <div className="text-center py-12">
          <Settings className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Aucune spécification disponible</p>
        </div>
      );
    }

    const isCategorized = Object.keys(specifications).some(key => 
      isObject(specifications[key])
    );

    if (!isCategorized) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(specifications).map(([key, value]) => (
            <div key={key} className="group">
              <div className="h-full bg-white hover:bg-gray-50 transition-colors duration-200 p-4 rounded-lg">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {key.replace(/_/g, ' ')}
                </p>
                <p className="text-base font-medium text-gray-900">{String(value)}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {Object.entries(specifications).map(([category, items]) => {
          const isExpanded = expandedSpecCategories[category] !== false;
          const hasItems = items && isObject(items) && Object.keys(items).length > 0;
          const icon = getCategoryIcon(category);
          const displayTitle = sectionTitles[category] || category;
          
          return (
            <div key={category} className="group">
              {/* Category Header */}
              <button
                type="button"
                onClick={() => toggleSpecCategory(category)}
                className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-all duration-200 rounded-lg mb-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg text-[#D4452C] group-hover:from-[#D4452C]/5 group-hover:to-[#D4452C]/10 transition-all duration-200">
                    {icon}
                  </div>
                  <div className="text-left">
                    <h4 className="font-semibold text-gray-900 text-base leading-tight">
                      {displayTitle}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {hasItems ? `${Object.keys(items).length} spécification${Object.keys(items).length > 1 ? 's' : ''}` : 'Non disponible'}
                    </p>
                  </div>
                </div>
                {hasItems && (
                  <div className={`flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </button>
              
              {/* Category Content */}
              {isExpanded && hasItems && (
                <div className="animate-fade-in">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pl-0 sm:pl-4 mb-6">
                    {Object.entries(items).map(([key, value]) => (
                      <div 
                        key={key} 
                        className="bg-white hover:bg-gray-50 transition-colors duration-200 p-4 rounded-lg"
                      >
                        <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          {key.replace(/_/g, ' ')}
                        </dt>
                        <dd className="text-base font-medium text-gray-900">
                          {String(value)}
                        </dd>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };


  const hasVersions = versions.length > 0;
  const currentItem = selectedVersion || model;
  const currentImages = currentItem?.images || [];
  const currentFeatures = currentItem?.features || [];
  const currentDescription = currentItem?.description || '';
  const currentPrice = selectedVersion ? selectedVersion.price : model?.starting_price;
  const currentEngine = selectedVersion ? selectedVersion.engine : model?.engine;
  const currentFuelType = selectedVersion ? selectedVersion.fuel_type : model?.fuel_type;
  const currentTransmission = selectedVersion ? selectedVersion.transmission : model?.transmission;
  const currentGarantie = selectedVersion ? selectedVersion.garantie : model?.garantie;

  return (
    <div className="min-h-screen bg-white">
      <style jsx global>{`
        body { background-color: white !important; }
      `}</style>
      <Header />
      
      <div className="bg-white -mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <Link to={`/neuf/${brandSlug}`} className="text-[#D4452C] hover:text-[#b83a24] mr-3 text-base flex items-center">
              <ChevronLeft className="w-5 h-5 mr-2" />
              Retour
            </Link>
            <div className="h-px w-full sm:h-5 sm:w-px bg-gray-300 sm:mx-3"></div>
          </div>
        </div>
      </div>

      <LayoutWithBanners
        pageLocation="car-prices"
        showTopBanner={true}
        showBottomBanner={true}
        showLeftSidebar={false}
        showRightSidebar={false}
        className="bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 bg-white">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-3 border-[#D4452C] border-t-transparent mx-auto mb-3"></div>
                <p className="text-sm text-gray-600">Chargement...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-50 rounded-full mb-3">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-red-600 mb-3 text-sm">{error}</p>
              <button onClick={loadModel} className="px-4 py-2 bg-[#D4452C] text-white rounded-lg hover:bg-[#b83a24] transition-colors text-sm">
                Réessayer
              </button>
            </div>
          ) : !model ? (
            <div className="text-center py-12">
              <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Modèle non trouvé</p>
            </div>
          ) : (
            <>
              {/* --- HEADER TITLE --- */}
              <div className="w-full px-4 sm:px-6 lg:px-8 relative z-10 mb-6 sm:mb-10">
                <div className="text-center mb-6 sm:mb-10">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 uppercase tracking-wide mb-3">
                    {selectedVersion ? selectedVersion.name : (model ? `${model.brand?.name || ''} ${model.name || ''}`.trim() : 'Chargement...')}
                  </h2>
                  <div className="w-16 sm:w-24 h-1 bg-red-600 mx-auto"></div>
                </div>
              </div>

              {/* --- IMAGE CAROUSEL --- */}
              <div className="mb-6">
                <div className="relative max-w-5xl mx-auto">
                  <div className="h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px] bg-white flex items-center justify-center">
                    {currentImages.length > 0 ? (
                      <img src={currentImages[currentImageIndex]} alt={currentItem?.name || model.name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-300">
                        <Car className="w-12 sm:w-16 h-12 sm:h-16 mb-2" />
                        <p className="text-sm">Aucune image</p>
                      </div>
                    )}
                  </div>
                  
                  {currentImages.length > 1 && (
                    <>
                      <button onClick={prevImage} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm text-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-white shadow-lg transition-all hover:scale-105">
                        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <button onClick={nextImage} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm text-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-white shadow-lg transition-all hover:scale-105">
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <div className="flex justify-center mt-3 space-x-1.5 sm:space-x-2">
                        {currentImages.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`h-1.5 sm:h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-[#D4452C] w-4 sm:w-6' : 'bg-gray-300 w-1.5 sm:w-2 hover:bg-gray-400'}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* --- PRICE & QUOTE BUTTON --- */}
              {currentPrice && (
                <div className="bg-gradient-to-r from-[#D4452C]/5 to-[#b83a24]/5 rounded-xl p-4 sm:p-5 mb-6 border border-[#D4452C]/10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center">
                      <div className="bg-[#D4452C]/10 p-3 rounded-lg mr-3">
                        <Tag className="w-5 h-5 text-[#D4452C]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">{selectedVersion ? 'Prix' : 'Prix de départ'}</p>
                        <p className="text-xl sm:text-2xl font-bold text-[#D4452C]">{formatPrice(currentPrice)}</p>
                      </div>
                    </div>
                    <button onClick={() => setShowQuoteModal(true)} className="bg-[#D4452C] text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg hover:bg-[#b83a24] transition-colors text-sm sm:text-base font-medium flex items-center justify-center">
                      <span>Demander un devis</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              )}

              {/* --- SCENARIO 1: NO SUB-VERSIONS (Standard View) --- */}
              {!hasVersions && (
                <>
                  <div className="border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8">
                      {['overview', 'specs', 'features'].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab as any)}
                          className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab ? 'border-[#D4452C] text-[#D4452C]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        >
                          {tab === 'overview' ? 'Aperçu' : tab === 'specs' ? 'Spécifications' : 'Équipements'}
                        </button>
                      ))}
                    </nav>
                  </div>

                  {/* ... (Existing Tabs Logic for No Versions) ... */}
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-4">
                        {[
                          { label: 'Moteur', value: currentEngine, icon: Gauge, color: 'blue' },
                          { label: 'Carburant', value: currentFuelType, icon: Fuel, color: 'green' },
                          { label: 'Transmission', value: currentTransmission, icon: Settings, color: 'purple' },
                          { label: 'Garantie', value: currentGarantie, icon: Shield, color: 'orange' },
                        ].map((item, i) => (
                          <div key={i} className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100/50 p-3 sm:p-4 rounded-xl border border-${item.color}-100 flex flex-col items-center`}>
                            <div className="flex items-center mb-2">
                              <item.icon className={`w-4 h-4 text-${item.color}-600 mr-2`} />
                              <p className={`text-sm font-medium text-${item.color}-600`}>{item.label}</p>
                            </div>
                            <p className="text-sm sm:text-base font-bold text-gray-900 truncate">{item.value || 'N/A'}</p>
                          </div>
                        ))}
                      </div>

                      {currentDescription && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                            <Info className="w-4 h-4 mr-2 text-[#D4452C]" /> Description
                          </h3>
                          <p className="text-sm text-gray-700 leading-relaxed">{currentDescription}</p>
                        </div>
                      )}

                      {getFormattedSpecifications() && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                            <Settings className="w-4 h-4 mr-2 text-[#D4452C]" /> Spécifications principales
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                            {Object.entries(getFormattedSpecifications()!).slice(0, 4).map(([category, items]) => (
                              <div key={category} className="mb-4">
                                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                  <div className="p-1 bg-[#D4452C]/10 rounded mr-2">
                                    <div className="text-[#D4452C] text-xs">{getCategoryIcon(category)}</div>
                                  </div>
                                  {sectionTitles[category] || category}
                                </h4>
                                {isObject(items) && (
                                  <div className="space-y-1">
                                    {Object.entries(items).slice(0, 3).map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="text-sm text-gray-600">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-sm font-medium text-gray-900">{String(value)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          <button onClick={() => setActiveTab('specs')} className="mt-4 text-[#D4452C] hover:text-[#b83a24] text-sm font-medium flex items-center">
                            Voir toutes les spécifications <ArrowRight className="w-4 h-4 ml-1" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'specs' && (
                    <div className="space-y-6">
                      {getFormattedSpecifications() && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                            <Settings className="w-4 h-4 mr-2 text-[#D4452C]" /> Spécifications techniques
                          </h4>
                          <SpecificationsDisplay specifications={getFormattedSpecifications()!} />
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'features' && (
                    <div className="space-y-6">
                      {currentFeatures.length > 0 && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                            <Package className="w-4 h-4 mr-2 text-[#D4452C]" /> Équipements
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {currentFeatures.map((feature, index) => (
                              <div key={index} className="flex items-center p-3 bg-gradient-to-r from-green-50 to-transparent rounded-lg">
                                <CheckCircle className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                                <span className="text-sm text-gray-700">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {['Équipements de sécurité', 'Aides à la conduite', 'Audio et communication'].map(cat => {
                        const specs = getFormattedSpecifications();
                        if (!specs || !specs[cat]) return null;
                        let icon = Shield;
                        let color = 'blue';
                        if (cat === 'Aides à la conduite') { icon = Cpu; color = 'purple'; }
                        if (cat === 'Audio et communication') { icon = Battery; color = 'orange'; }
                        const IconComponent = icon;
                        return (
                          <div key={cat} className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                            <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                              <IconComponent className="w-4 h-4 mr-2 text-[#D4452C]" />
                              {sectionTitles[cat] || cat}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {Object.entries(specs[cat]).map(([key, value]) => (
                                <div key={key} className={`flex items-center p-3 bg-gradient-to-r from-${color}-50 to-transparent rounded-lg`}>
                                  <CheckCircle className={`w-4 h-4 text-${color}-500 mr-3 flex-shrink-0`} />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{key.replace(/_/g, ' ')}</p>
                                    {value && <p className="text-xs text-gray-600">{String(value)}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* --- SCENARIO 2: MAIN MODEL PAGE WITH VERSIONS LIST --- */}
              {hasVersions && !selectedVersion && (
                <>
                  {model.description && (
                    <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100 mb-6">
                      <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                        <Info className="w-4 h-4 mr-2 text-[#D4452C]" /> Description
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed">{model.description}</p>
                    </div>
                  )}

                  <div className="mt-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                      <h3 className="text-lg font-bold text-gray-900">Versions disponibles</h3>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium self-start sm:self-auto">
                        {versions.length} version{versions.length > 1 ? 's' : ''}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {versions.map((version) => (
                        <Link
                          key={version.id}
                          to={`/neuf/${brandSlug}/${slugify(model.name)}/${slugify(version.name)}`}
                          className="bg-white rounded-xl overflow-hidden border border-gray-100 group hover:shadow-md transition-shadow"
                        >
                          <div className="relative h-40 sm:h-48 overflow-hidden bg-white">
                            {version.images && version.images.length > 0 ? (
                              <img src={version.images[0]} alt={version.name} className="w-full h-full object-contain" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-50">
                                <Car className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="absolute bottom-3 left-3 right-3">
                                <p className="text-white text-sm font-medium flex items-center">
                                  <Info className="w-4 h-4 mr-2" /> Voir détails
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <h4 className="font-bold text-base text-gray-900 mb-2 line-clamp-1">{version.name}</h4>
                            {version.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{version.description}</p>}
                            <p className="text-lg font-bold text-[#D4452C] mb-3">{formatPrice(version.price)}</p>
                            <div className="flex flex-wrap gap-2">
                              {version.engine && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">{version.engine}</span>}
                              {version.transmission && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">{version.transmission}</span>}
                              {version.garantie && <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">{version.garantie}</span>}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* --- SCENARIO 3: SPECIFIC VERSION SELECTED --- */}
              {hasVersions && selectedVersion && (
                <>
                  <div className="border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8">
                      {['overview', 'specs', 'features'].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab as any)}
                          className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab ? 'border-[#D4452C] text-[#D4452C]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                        >
                          {tab === 'overview' ? 'Aperçu' : tab === 'specs' ? 'Spécifications' : 'Équipements'}
                        </button>
                      ))}
                    </nav>
                  </div>

                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-4">
                        {[
                          { label: 'Moteur', value: currentEngine, icon: Gauge, color: 'blue' },
                          { label: 'Carburant', value: currentFuelType, icon: Fuel, color: 'green' },
                          { label: 'Transmission', value: currentTransmission, icon: Settings, color: 'purple' },
                          { label: 'Garantie', value: currentGarantie, icon: Shield, color: 'orange' },
                        ].map((item, i) => (
                          <div key={i} className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100/50 p-3 sm:p-4 rounded-xl border border-${item.color}-100 flex flex-col items-center`}>
                            <div className="flex items-center mb-2">
                              <item.icon className={`w-4 h-4 text-${item.color}-600 mr-2`} />
                              <p className={`text-sm font-medium text-${item.color}-600`}>{item.label}</p>
                            </div>
                            <p className="text-sm sm:text-base font-bold text-gray-900 truncate">{item.value || 'N/A'}</p>
                          </div>
                        ))}
                      </div>

                      {currentDescription && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                            <Info className="w-4 h-4 mr-2 text-[#D4452C]" /> Description
                          </h3>
                          <p className="text-sm text-gray-700 leading-relaxed">{currentDescription}</p>
                        </div>
                      )}

                      {getFormattedSpecifications() && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                            <Settings className="w-4 h-4 mr-2 text-[#D4452C]" /> Spécifications principales
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                            {Object.entries(getFormattedSpecifications()!).slice(0, 4).map(([category, items]) => (
                              <div key={category} className="mb-4">
                                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                  <div className="p-1 bg-[#D4452C]/10 rounded mr-2">
                                    <div className="text-[#D4452C] text-xs">{getCategoryIcon(category)}</div>
                                  </div>
                                  {sectionTitles[category] || category}
                                </h4>
                                {isObject(items) && (
                                  <div className="space-y-1">
                                    {Object.entries(items).slice(0, 3).map(([key, value]) => (
                                      <div key={key} className="flex justify-between">
                                        <span className="text-sm text-gray-600">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-sm font-medium text-gray-900">{String(value)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          <button onClick={() => setActiveTab('specs')} className="mt-4 text-[#D4452C] hover:text-[#b83a24] text-sm font-medium flex items-center">
                            Voir toutes les spécifications <ArrowRight className="w-4 h-4 ml-1" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'specs' && (
                    <div className="space-y-6">
                      {getFormattedSpecifications() && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                            <Settings className="w-4 h-4 mr-2 text-[#D4452C]" /> Spécifications techniques
                          </h4>
                          <SpecificationsDisplay specifications={getFormattedSpecifications()!} />
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'features' && (
                    <div className="space-y-6">
                      {currentFeatures.length > 0 && (
                        <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                          <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                            <Package className="w-4 h-4 mr-2 text-[#D4452C]" /> Équipements
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {currentFeatures.map((feature, index) => (
                              <div key={index} className="flex items-center p-3 bg-gradient-to-r from-green-50 to-transparent rounded-lg">
                                <CheckCircle className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                                <span className="text-sm text-gray-700">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {['Équipements de sécurité', 'Aides à la conduite', 'Audio et communication'].map(cat => {
                        const specs = getFormattedSpecifications();
                        if (!specs || !specs[cat]) return null;
                        let icon = Shield;
                        let color = 'blue';
                        if (cat === 'Aides à la conduite') { icon = Cpu; color = 'purple'; }
                        if (cat === 'Audio et communication') { icon = Battery; color = 'orange'; }
                        const IconComponent = icon;
                        return (
                          <div key={cat} className="bg-white rounded-xl p-4 sm:p-5 border border-gray-100">
                            <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center">
                              <IconComponent className="w-4 h-4 mr-2 text-[#D4452C]" />
                              {sectionTitles[cat] || cat}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {Object.entries(specs[cat]).map(([key, value]) => (
                                <div key={key} className={`flex items-center p-3 bg-gradient-to-r from-${color}-50 to-transparent rounded-lg`}>
                                  <CheckCircle className={`w-4 h-4 text-${color}-500 mr-3 flex-shrink-0`} />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{key.replace(/_/g, ' ')}</p>
                                    {value && <p className="text-xs text-gray-600">{String(value)}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </LayoutWithBanners>
      
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">Demander un devis</h3>
              <button onClick={() => setShowQuoteModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              {/* SUCCESS MESSAGE BLOCK */}
{quoteSuccess && (
    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start animate-fade-in">
      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
      <div>
        <h4 className="text-sm font-bold text-green-800">Demande envoyée !</h4>
        <p className="text-sm text-green-700 mt-1">
          Votre demande a été transmise avec succès. Un conseiller vous contactera dans les plus brefs délais.
        </p>
      </div>
    </div>
  )}

  {/* ERROR POP-UP MESSAGE */}
  {quoteError && (
    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start animate-fade-in">
      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
      <div>
        <h4 className="text-sm font-bold text-red-800">Erreur</h4>
        <p className="text-sm text-red-700 mt-1">{quoteError}</p>
      </div>
    </div>
  )}
<form onSubmit={handleQuoteSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><User className="h-4 w-4 text-gray-400" /></div>
                      <input type="text" value={quoteForm.lastName} onChange={(e) => setQuoteForm({ ...quoteForm, lastName: e.target.value })} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm" required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><User className="h-4 w-4 text-gray-400" /></div>
                      <input type="text" value={quoteForm.firstName} onChange={(e) => setQuoteForm({ ...quoteForm, firstName: e.target.value })} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm" required />
                    </div>
                  </div>
                </div>
                {/* NEW COMPANY FIELD */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise <span className="text-gray-400 font-normal">(Optionnel)</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Briefcase className="h-4 w-4 text-gray-400" /></div>
                    <input type="text" value={quoteForm.companyName} onChange={(e) => setQuoteForm({ ...quoteForm, companyName: e.target.value })} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm" placeholder="Ex: Ma Société SARL" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Phone className="h-4 w-4 text-gray-400" /></div>
                    <input type="tel" value={quoteForm.phone} onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm" required />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Mail className="h-4 w-4 text-gray-400" /></div>
                    <input type="email" value={quoteForm.email} onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm" required />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><MapPin className="h-4 w-4 text-gray-400" /></div>
                    <input type="text" value={quoteForm.city} onChange={(e) => setQuoteForm({ ...quoteForm, city: e.target.value })} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm" required />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Votre message</label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none"><MessageSquare className="h-4 w-4 text-gray-400" /></div>
                    <textarea value={quoteForm.message} onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })} rows={4} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:border-[#D4452C] focus:ring-1 focus:ring-[#D4452C] focus:ring-opacity-20 text-sm resize-none" placeholder="Décrivez votre demande..." />
                  </div>
                </div>
                
                {/* 3. RECAPTCHA WIDGET */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code de vérification <span className="text-red-500">*</span></label>
                  <div className="bg-gray-50 p-2 rounded-md flex justify-center">
                    <ReCAPTCHA
                      sitekey="6LdcbzwsAAAAAF3vCnHZh4xu8uONptbkWb7zRMIM" // REPLACE THIS!
                      onChange={handleRecaptchaChange}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end pt-2">
                  <button type="button" onClick={() => setShowQuoteModal(false)} className="mr-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition-colors text-sm">Annuler</button>
                  <button type="submit" disabled={quoteSubmitting} className="px-4 py-2 bg-[#D4452C] text-white rounded-md font-medium hover:bg-[#b83a24] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm">
                    {quoteSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}